import React, { useEffect, useState } from "react";
import { fetchAPI } from "../../utils/api";
import { OperatorDashboard } from "../interfaces/OperatorsBalanceDashboard";
import { Alert, AlertTitle, Box, CircularProgress, Container, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { OperatorsBalanceDashboardTable } from "./OperatorsBalanceDashboardTable";
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InstitutionAttributesMap } from "../interfaces/Institution";



export default function OperatorsBalanceDashboardTab() {
    const [operatorDashboard, setOperatorDashboard] = useState({} as OperatorDashboard)
    const [attributes, setAttributes] = useState<InstitutionAttributesMap | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);


    useEffect(() => {
        initData();
    }, []);

    async function initData(forceRefresh = false) {
        setIsLoading(true);
        setError(null);

        const dashboardUrl = `/operators/infos${forceRefresh ? '?forceRefresh=true' : ''}`;
        const attributesUrl = `/operators/attributes`;
        const headers = { 'institution': 'LEMONWAY' };

        // GET DASHBOARD
        const fetchDashboard = fetchAPI(dashboardUrl, { headers })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Dashboard Error: ${res.status}`);
                const json = await res.json();
                setOperatorDashboard(json);
            })
            .catch((err) => {
                setError(err.message);
            });

        // GET ATTRIBUTES
        const fetchAttributes = fetchAPI(attributesUrl, { headers })
            .then(async (res) => {
                if (res.ok) {
                    const json = await res.json();
                    setAttributes(json);
                }
            })
            .catch(() => {
                console.warn("Could not fetch attributes for external links.");
            });

        await Promise.allSettled([fetchDashboard, fetchAttributes]);
        setIsLoading(false);
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 5, minHeight: '70vh' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: 1,
                    borderColor: 'grey.300',
                    pb: 2,
                    mb: 3
                }}>
                    <Typography variant="h3" component="h1">
                        Operators Dashboard
                    </Typography>

                    <Tooltip title="Force refresh (DB)">
                        <IconButton
                            onClick={() => initData(true)}
                            disabled={isLoading}
                            size="large"
                            color="primary"
                            sx={{
                                mt: 0.5,
                                transition: 'transform 0.3s ease',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                        >
                            <RefreshIcon
                                fontSize="inherit"
                                sx={{
                                    animation: isLoading ? 'spin 1s linear infinite' : 'none'
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="large" />}
                    sx={{ mb: 4, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                >
                    <AlertTitle sx={{ fontWeight: 800 }}>Dashboard Overview</AlertTitle>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        This dashboard provides a reconciliation between total pending flows (transfers and payments) and your current Lemonway balances. It helps alert to potential insufficient funds on operator wallets ahead of the next payout.
                    </Typography>
                    <Divider sx={{ my: 1.5, opacity: 0.6 }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <strong>Note:</strong> Data is displayed as of the current time with a 5-minute cache. Please click the <strong>Refresh</strong> button to trigger a real-time recalculation of balances and flows.
                    </Typography>
                </Alert>

                {isLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 5, p: 3 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Data retrieval in progress, this may take a moment...
                        </Typography>
                    </Box>
                )}
                {error && (
                    <Typography color="error" sx={{ mt: 3, p: 2, border: 1, borderColor: 'error.main', borderRadius: 1 }}>
                        Error: {error}
                    </Typography>
                )}
                {!isLoading && !operatorDashboard && !error && (
                    <Typography sx={{ mt: 3, color: 'text.secondary', textAlign: 'center' }}>
                        No Data.
                    </Typography>
                )}
                {!isLoading && operatorDashboard && (
                    <OperatorsBalanceDashboardTable operatorDashboard={operatorDashboard} attributes={attributes} />
                )}
            </Box>
        </Container>
    );
}